import { List, Map, Iterable } from "immutable";
import { Item, Kinds, MANUFACTURED_SIZES_DELIMITER, SELECTED_SIZES_DELIMITER } from "constants/product-collection";
import { ISchemaKey } from "helpers/product-collection";
import { amphibiousGetIn, amphibiousSetIn } from "helpers/objUtils";
import { findOneFieldByKind } from "./allocation";
import {
    AllocationBag,
    AllocationBagType,
    BAG_VALUE_PARAMS_SEPARATOR,
    getBagValuesByRowId,
} from "helpers/allocation-dimension";
import { safeParseFloat } from "lib/number";

export const shouldSyncSelectedSizes = (itemDisplayProperties: List<ISchemaKey> | Array<ISchemaKey>) => {
    const selectedSizesColumn = findOneFieldByKind(
        Kinds.SIZES_SELECTED,
        Iterable.isIterable(itemDisplayProperties)
            ? (itemDisplayProperties as List<ISchemaKey>).toJS()
            : itemDisplayProperties,
    );
    if (!selectedSizesColumn) {
        return false;
    }

    const { lookup_plan } = selectedSizesColumn;
    // We ONLY auto-populate selected sizes when lookup_plan is empty
    if (lookup_plan) {
        return false;
    }

    return true;
};

export const syncSelectedSizes = <I extends Map<string, any> | { [field: string]: any }>(
    item: I,
    itemDisplayProperties: List<ISchemaKey> | Array<ISchemaKey>,
    override?: boolean,
) => {
    const selectedSizesColumn = findOneFieldByKind(
        Kinds.SIZES_SELECTED,
        Iterable.isIterable(itemDisplayProperties)
            ? (itemDisplayProperties as List<ISchemaKey>).toJS()
            : itemDisplayProperties,
    );
    if (selectedSizesColumn) {
        const sourceValue = amphibiousGetIn(item, Item.FIELDS.NU_SIZES_MANUFACTURED);
        const targetFieldDelimiter = selectedSizesColumn.field_value_delimiter || SELECTED_SIZES_DELIMITER;

        // Only copy over empty field OR when `override` flag is true
        const isTargetFieldEmpty = !amphibiousGetIn(item, Item.FIELDS.SIZES_SELECTED);
        if ((isTargetFieldEmpty || override) && sourceValue) {
            const sourceSizes = sourceValue.split(MANUFACTURED_SIZES_DELIMITER);
            const targetSizes = sourceSizes.join(targetFieldDelimiter);

            return amphibiousSetIn(item, Item.FIELDS.SIZES_SELECTED, targetSizes, true);
        }
    }

    return item;
};

const sizeLevelFields = [Item.FIELDS.NRF_SIZE_CODE, Item.FIELDS.NRF_SIZE_DESCRIPTION, Item.FIELDS.UPC];

export const applySizeLevelFieldsChanges = (
    currentItem: Map<string, any>,
    updatedItem: Map<string, any>,
    oldItem: Map<string, any>,
    forceNewValue = false,
) =>
    sizeLevelFields.reduce((acc, fieldPath) => {
        const newValue: string = updatedItem.getIn(fieldPath) ?? "";
        const oldValue: string = oldItem.getIn(fieldPath) ?? "";
        if (newValue !== oldValue || forceNewValue) {
            return acc.setIn(fieldPath, newValue);
        }
        return acc;
    }, currentItem);

/**
 * Helper function to reconcile quantities from current bag.
 * It removes existing quantity if it doesn't exist in `allocations`, add new ones or replace existing.
 * @param allocations
 * @param rowId
 * @param allocationBag
 * @param bagType
 * @returns
 */
export const generateAllocationBagMutations = (
    allocations: string[][],
    rowId: string,
    allocationBag: AllocationBag,
    bagType: AllocationBagType,
) => {
    const bagValueMutations = [];

    const itemId = rowId;
    const existingBagValues = getBagValuesByRowId(bagType, allocationBag, itemId);

    const mappedQuantities = allocations.reduce((acc, allocation) => {
        const breadcrumb = [...allocation];
        const quantity = safeParseFloat(breadcrumb.pop(), 0);

        acc[breadcrumb.join(BAG_VALUE_PARAMS_SEPARATOR)] = quantity;
        return acc;
    }, {});

    existingBagValues.forEach((_bagValue, key) => {
        const breadcrumb = key.split(BAG_VALUE_PARAMS_SEPARATOR);

        // Remove if it existed before
        if (!mappedQuantities[key]) {
            bagValueMutations.push({
                rowId: itemId,
                bagType,
                dimensionValues: breadcrumb,
                quantity: 0,
            });
        } else {
            bagValueMutations.push({
                rowId: itemId,
                bagType,
                dimensionValues: breadcrumb,
                quantity: mappedQuantities[key],
            });

            // Remove it from map so it won't be processed twice
            delete mappedQuantities[key];
        }
    });

    Object.keys(mappedQuantities).forEach((key) => {
        const breadcrumb = key.split(BAG_VALUE_PARAMS_SEPARATOR);
        bagValueMutations.push({
            rowId: itemId,
            bagType,
            dimensionValues: breadcrumb,
            quantity: mappedQuantities[key],
        });
    });

    return bagValueMutations;
};
